/* eslint-disable react/jsx-props-no-spreading */
import { ChangeEvent } from 'react'
// import { PaginationItem as PaginationItemMui } from '@mui/lab'
import { Tooltip } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Pagination from '@mui/material/Pagination'
import PaginationItem from '@mui/material/PaginationItem'
import makeStyles from '@mui/styles/makeStyles'
import {
  AngleLeftIcon,
  AngleRightIcon,
  FirstPageIcon,
  LastPageIcon
} from 'src/components/shared/ui/Icons'

import { theme } from 'src/providers/ThemeProvider'

const useStyles = makeStyles({
  root: {
    flexShrink: 0,

    '& .MuiIconButton-root': {
      display: 'flex',
      width: 22,
      height: 22,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 4,

      '&:hover': {
        backgroundColor: theme.colors.white01
      },

      '& span': {
        fontSize: '14px',
        color: theme.colors.gray14
      }
    }
  }
})

const useItemStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    fontFamily: theme.corporate.primaryFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
    color: theme.colors.gray14,

    '&.Mui-selected': {
      backgroundColor: theme.colors.blue07
    },

    '& .MuiTouchRipple-root': {
      display: 'none'
    }
  }
}))

function PaginationItemMu(props) {
  const classes = useItemStyles()

  return <PaginationItem {...props} classes={classes} />
}

// eslint-disable-next-line react/prop-types
type Props = {
  count: number
  onChangePage: (event: ChangeEvent<any>, page: number) => void
  page: number
  rowsPerPage: number
}

function TablePaginationActions({ count, onChangePage, page, rowsPerPage }: Props): JSX.Element {
  const classes = useStyles()
  const totalPages = Math.ceil(count / rowsPerPage)

  function onChange(event: ChangeEvent<any>, selectedPage: number) {
    onChangePage(event as React.MouseEvent<HTMLButtonElement>, selectedPage - 1)
  }

  function getSiblingCount(): number {
    switch (page) {
      case 0:
      case 1:
      case 2:
      case totalPages - 1:
      case totalPages - 2:
      case totalPages - 3:
        return 1
      default:
        return 2
    }
  }

  function renderItem({ onClick, disabled, type, ...rest }) {
    if (type === 'first') {
      return (
        <Tooltip arrow title="Ir a la primera página" placement="bottom">
          <span>
            <IconButton onClick={onClick} disabled={disabled} aria-label="Ir a la primera página">
              <FirstPageIcon fontSize="small" htmlColor={theme.corporate.primaryColor} />
            </IconButton>
          </span>
        </Tooltip>
      )
    }
    if (type === 'last') {
      return (
        <Tooltip arrow title="Ir a la última página" placement="bottom">
          <span>
            <IconButton onClick={onClick} disabled={disabled} aria-label="Ir a la última página">
              <LastPageIcon fontSize="small" htmlColor={theme.corporate.primaryColor} />
            </IconButton>
          </span>
        </Tooltip>
      )
    }

    if (type === 'previous') {
      return (
        <Tooltip arrow title="Ir a la página anterior" placement="bottom">
          <span>
            <IconButton onClick={onClick} disabled={disabled} aria-label="Ir a la página anterior">
              <AngleLeftIcon fontSize="small" htmlColor={theme.corporate.primaryColor} />
            </IconButton>
          </span>
        </Tooltip>
      )
    }

    if (type === 'next') {
      return (
        <Tooltip arrow title="Ir a la página siguiente" placement="bottom">
          <span>
            <IconButton onClick={onClick} disabled={disabled} aria-label="Ir a la página siguiente">
              <AngleRightIcon fontSize="small" htmlColor={theme.corporate.primaryColor} />
            </IconButton>
          </span>
        </Tooltip>
      )
    }

    if (type === 'start-ellipsis') {
      return (
        <Tooltip arrow title="5 páginas anteriores" placement="bottom">
          <IconButton
            onClick={e => {
              onChange(e, Math.max(page - 4, 1))
            }}
            classes={classes}
          >
            <span>...</span>
          </IconButton>
        </Tooltip>
      )
    }

    if (type === 'end-ellipsis') {
      return (
        <Tooltip arrow title="5 páginas siguientes" placement="bottom">
          <IconButton
            onClick={e => {
              onChange(e, Math.min(page + 6, totalPages))
            }}
            classes={classes}
          >
            <span>...</span>
          </IconButton>
        </Tooltip>
      )
    }

    return <PaginationItemMu {...rest} onClick={onClick} disabled={disabled} type={type} />
  }

  return (
    <div className={classes.root}>
      <Pagination
        page={page + 1}
        count={totalPages}
        showFirstButton
        showLastButton
        onChange={onChange}
        renderItem={renderItem}
        size="small"
        siblingCount={getSiblingCount()}
        boundaryCount={0}
      />
    </div>
  )
}

export default TablePaginationActions
